
import { useRef } from "react";
import { FileText, ImageIcon, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const ACCEPTED_TYPES =
  "image/jpeg,image/png,image/gif,image/webp,application/pdf";

export const ACCEPTED_TYPES_SET = new Set(ACCEPTED_TYPES.split(","));
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface FileUploadProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  maxFiles?: number;
  maxSizeBytes?: number;
  disabled?: boolean;
}

export function FileUpload({
  files,
  onFilesChange,
  maxFiles = 5,
  maxSizeBytes = 10 * 1024 * 1024,
  disabled,
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const errorRef = useRef<string | null>(null);

  const handleSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (!selected.length) return;

    const remaining = maxFiles - files.length;
    if (selected.length > remaining) {
      errorRef.current = `You can attach up to ${maxFiles} files.`;
      // force re-render by updating files with same array
      onFilesChange([...files]);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    const oversized = selected.find((f) => f.size > maxSizeBytes);
    if (oversized) {
      errorRef.current = `"${oversized.name}" exceeds ${formatFileSize(maxSizeBytes)} limit.`;
      onFilesChange([...files]);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }

    errorRef.current = null;
    onFilesChange([...files, ...selected]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    errorRef.current = null;
    onFilesChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        disabled={disabled || files.length >= maxFiles}
        onClick={() => inputRef.current?.click()}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Paperclip className="h-3.5 w-3.5" />
        Attach files
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES}
        multiple
        className="hidden"
        onChange={handleSelect}
        aria-label="Upload files"
      />

      {errorRef.current && (
        <p className="text-xs text-destructive">{errorRef.current}</p>
      )}

      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((file, i) => (
            <div
              key={`${file.name}-${i}`}
              className="flex items-center gap-1.5 rounded-md border bg-muted/50 px-2 py-1 text-xs"
            >
              {file.type.startsWith("image/") ? (
                <img
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  className="h-6 w-6 rounded object-cover"
                />
              ) : (
                <FileText className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="max-w-[120px] truncate">{file.name}</span>
              <span className="text-muted-foreground">
                {formatFileSize(file.size)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-4 w-4 p-0"
                onClick={() => removeFile(i)}
                disabled={disabled}
                aria-label="Remove file"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
